import { useMemo, useState, useEffect } from 'react';
import type { Section, Answers, PageStyle } from '../types';
import { Blocks } from '../components/Blocks';
import { getEncouragement } from '../lib/encouragement';

interface Props {
  sections: Section[];
  onComplete: (answers: Answers) => void;
  /** Question IDs to skip (e.g. from prior-submission check) */
  skipQuestionIds?: string[];
  /** Transition message shown before first question when questions were skipped */
  transitionMessage?: string;
  /** Survey mode (AssessmentConfig.surveyMode): suppress scorecard encouragement copy. */
  surveyMode?: boolean;
  /** Assessment type — 'registration' (event surveys) gets its own encouragement wording. */
  assessmentType?: string;
}

/**
 * Conditional visibility. MIRRORS worker/src/show-if.ts — the taker cannot import
 * from worker/, and that copy is authoritative (the submit path prunes answers
 * with it, so a stale client can never store an answer to a question it should
 * not have shown). This copy decides only what to RENDER. Keep them in step;
 * the worker one has the tests. Fails CLOSED so a hidden gate cascades.
 */
function isVisible(q: { showIf?: Record<string, Array<string | number>> }, answers: Answers): boolean {
  const gates = q.showIf;
  if (!gates) return true;
  const keys = Object.keys(gates);
  if (keys.length === 0) return true;
  return keys.every((gateId) => {
    const allowed = gates[gateId];
    if (!Array.isArray(allowed) || allowed.length === 0) return false;
    const given = answers[gateId];
    // Multi-select gate (array): passes when ANY pick is allowed; no picks = unanswered.
    const picked = (Array.isArray(given) ? given : [given]).filter((v) => v !== null && v !== undefined && v !== '');
    if (picked.length === 0) return false;
    return picked.some((v) => allowed.some((a) => String(a) === String(v)));
  });
}

/** The value an option stores when picked. Mirrors worker/src/multi-select.ts. */
function storedValue(opt: { value?: number; archetype?: string; correct?: boolean }): number | string {
  return opt.value ?? opt.archetype ?? (opt.correct ? 1 : 0);
}

export default function Questions({ sections, onComplete, skipQuestionIds, transitionMessage, surveyMode, assessmentType }: Props) {
  const skipSet = useMemo(() => new Set(skipQuestionIds ?? []), [skipQuestionIds]);

  const flat = useMemo(() => {
    const out: Array<{ section: Section; qi: number }> = [];
    sections.forEach((s) =>
      s.questions.forEach((_q, qi) => {
        if (!skipSet.has(s.questions[qi].id)) {
          out.push({ section: s, qi });
        }
      }),
    );
    return out;
  }, [sections, skipSet]);

  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [encouragement, setEncouragement] = useState<string | null>(null);
  const [textValue, setTextValue] = useState('');
  const [showTransition, setShowTransition] = useState(!!transitionMessage);
  const current = flat[idx];
  const q = current.section.questions[current.qi];

  // Which positions in `flat` are on the path the visitor is actually taking,
  // given what they have answered so far. `flat` stays the full ordered list —
  // only navigation and the counter are answer-dependent, so `idx` never dangles.
  //
  // ★ The counter shows the path you are ON, not every question that exists.
  // Gates only ever reference EARLIER questions, so by the time one matters it is
  // already decided; the denominator can grow when you open a follow-up, which
  // reads as a consequence of the answer you just gave rather than a number
  // jumping about. It shipped as a flat "of 17" — every registrant was told the
  // survey was twice the length they would actually be asked.
  const visiblePositions = useMemo(
    () => flat.map((f, i) => (isVisible(f.section.questions[f.qi], answers) ? i : -1)).filter((i) => i >= 0),
    [flat, answers],
  );
  const total = Math.max(visiblePositions.length, 1);
  const posInVisible = Math.max(visiblePositions.indexOf(idx), 0);
  const isText = q.type === 'text';
  const isMulti = q.type === 'multi';
  const answeredNow = Array.isArray(answers[q.id]) ? (answers[q.id] as unknown[]).length > 0 : answers[q.id] != null;
  const progress = ((posInVisible + (answeredNow ? 1 : 0)) / total) * 100;

  // Dismiss transition message after 4 seconds or on first interaction
  useEffect(() => {
    if (showTransition) {
      const t = setTimeout(() => setShowTransition(false), 4000);
      return () => clearTimeout(t);
    }
  }, [showTransition]);

  // Show encouragement on index change. Survey mode shows none; event
  // registration surveys get their own gap-free wording (lib/encouragement.ts).
  useEffect(() => {
    if (showTransition) return;
    const msg = surveyMode ? null : getEncouragement(idx, total, assessmentType);
    if (msg) {
      setEncouragement(msg);
      const timer = setTimeout(() => setEncouragement(null), 2500);
      return () => clearTimeout(timer);
    } else {
      setEncouragement(null);
    }
  }, [idx, total, showTransition, surveyMode, assessmentType]);

  // Apply per-question page style. Body bg / gradient are theme-owned
  // (see App.tsx comment on pageStyle effect) — we only honor backgroundImageUrl.
  useEffect(() => {
    const ps: PageStyle | undefined = q.pageStyle;
    if (!ps) return;
    const body = document.body;
    const prevImg = body.style.backgroundImage;
    if (ps.backgroundImageUrl && !ps.backgroundGradient) {
      body.style.backgroundImage = `url("${ps.backgroundImageUrl}")`;
    }
    return () => {
      body.style.backgroundImage = prevImg;
    };
  }, [q]);

  // Reset text input when question changes
  useEffect(() => {
    if (isText) {
      setTextValue((answers[q.id] as string) ?? '');
    }
  }, [idx, isText, q.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const advance = (next: Answers) => {
    setShowTransition(false);
    // Re-evaluate against `next`, not `answers` — the answer just given is
    // usually the very gate that decides whether the following question shows.
    let n = idx + 1;
    while (n < flat.length && !isVisible(flat[n].section.questions[flat[n].qi], next)) n++;
    if (n >= flat.length) {
      // Submit only what they were actually asked. Going back and changing a gate
      // can strand an answer to a now-hidden question; storing it would let a
      // question the visitor never saw feed scoring and routing.
      const shown: Answers = {};
      for (const f of flat) {
        const fq = f.section.questions[f.qi];
        if (fq.id in next && isVisible(fq, shown)) shown[fq.id] = next[fq.id];
      }
      onComplete(shown);
    } else {
      setIdx(n);
    }
  };

  const select = (value: number | string) => {
    const next: Answers = { ...answers, [q.id]: value };
    setAnswers(next);
    setTimeout(() => advance(next), 300);
  };

  // ★ Multi-select can't auto-advance like select() — the visitor is still
  // choosing — so picks toggle and an explicit Continue moves on. A pick past
  // maxSelect is refused; the worker cuts it too (multi-select.ts), this is the UX.
  const picks: Array<number | string> = isMulti && Array.isArray(answers[q.id]) ? (answers[q.id] as Array<number | string>) : [];
  const maxSelect = isMulti && q.maxSelect && q.maxSelect > 0 ? q.maxSelect : Infinity;
  const togglePick = (value: number | string) => {
    const has = picks.some((p) => String(p) === String(value));
    if (!has && picks.length >= maxSelect) return;
    const nextPicks = has ? picks.filter((p) => String(p) !== String(value)) : [...picks, value];
    setAnswers({ ...answers, [q.id]: nextPicks });
  };
  const submitMulti = () => {
    if (picks.length === 0) return;
    advance({ ...answers, [q.id]: picks });
  };

  const submitText = () => {
    if (!textValue.trim()) return;
    const next: Answers = { ...answers, [q.id]: textValue.trim() };
    setAnswers(next);
    advance(next);
  };

  const skip = () => {
    const next: Answers = { ...answers, [q.id]: '' };
    setAnswers(next);
    advance(next);
  };

  const goBack = () => {
    let p = idx - 1;
    while (p >= 0 && !isVisible(flat[p].section.questions[flat[p].qi], answers)) p--;
    if (p >= 0) setIdx(p);
  };

  const heroImage = q.pageStyle?.heroImageUrl;
  const selected = answers[q.id];

  return (
    <>
      <div className="progress">
        <div className="progress-track">
          <div className="progress-fill" style={{ width: `${progress}%` }} />
        </div>
        <div className="progress-label">
          Question {posInVisible + 1} of {total}
        </div>
      </div>

      {/* Transition message when questions were skipped */}
      {showTransition && transitionMessage && (
        <div className="encouragement transition-msg" key="transition">
          {transitionMessage}
        </div>
      )}

      {/* Encouragement toast */}
      {!showTransition && encouragement && (
        <div className="encouragement" key={idx}>
          {encouragement}
        </div>
      )}

      {heroImage && (
        <div className="page-hero">
          <img src={heroImage} alt="" />
        </div>
      )}

      <div className="card question-card">
        <div className="section-label">{current.section.label}</div>

        <Blocks blocks={q.blocksBefore} />

        {/* Question intro / VOC copy */}
        {q.intro && (
          <div className="question-intro">{q.intro}</div>
        )}

        <div className="question-text">{q.text}</div>
        {q.helpText && <div className="question-help">{q.helpText}</div>}

        {isText ? (
          /* Free-text question */
          <div className="text-question">
            <textarea
              className="text-input"
              value={textValue}
              onChange={(e) => {
                const v = q.maxLength ? e.target.value.slice(0, q.maxLength) : e.target.value;
                setTextValue(v);
              }}
              placeholder="Type your answer here..."
              rows={3}
              autoFocus
            />
            {q.maxLength && (
              <div className="text-counter">
                {textValue.length}/{q.maxLength}
              </div>
            )}
            <button
              className="btn btn-primary"
              onClick={submitText}
              disabled={!textValue.trim()}
              style={{ marginTop: 12 }}
            >
              Continue
            </button>
          </div>
        ) : isMulti ? (
          /* Multi-select question — toggle picks, then Continue */
          <div className="multi-question">
            {Number.isFinite(maxSelect) && (
              <div className="question-help multi-cap" aria-live="polite">
                {picks.length >= maxSelect
                  ? `That's your ${maxSelect} — tap one again to change it.`
                  : `Choose up to ${maxSelect}.`}
              </div>
            )}
            {q.options.map((opt, i) => {
              const value = storedValue(opt);
              const isPicked = picks.some((p) => String(p) === String(value));
              const atCap = !isPicked && picks.length >= maxSelect;
              return (
                <button
                  key={i}
                  type="button"
                  role="checkbox"
                  aria-checked={isPicked}
                  aria-disabled={atCap}
                  className={`option${isPicked ? ' selected' : ''}${atCap ? ' option-at-cap' : ''}`}
                  onClick={() => togglePick(value)}
                >
                  <span className="option-check" aria-hidden="true">{isPicked ? '✓' : ''}</span>
                  {opt.icon && <span style={{ marginRight: 10 }}>{opt.icon}</span>}
                  <span style={{ fontWeight: 500 }}>{opt.label}</span>
                  {opt.description && (
                    <div className="option-desc">{opt.description}</div>
                  )}
                </button>
              );
            })}
            <button
              className="btn btn-primary"
              onClick={submitMulti}
              disabled={picks.length === 0}
              style={{ marginTop: 12 }}
            >
              Continue
            </button>
          </div>
        ) : (
          /* Multiple-choice question */
          <div>
            {q.options.map((opt, i) => {
              const value = opt.value ?? opt.archetype ?? (opt.correct ? 1 : 0);
              const isSelected = selected === value;
              return (
                <button
                  key={i}
                  className={`option${isSelected ? ' selected' : ''}`}
                  onClick={() => select(value)}
                >
                  {opt.icon && <span style={{ marginRight: 10 }}>{opt.icon}</span>}
                  {opt.imageUrl && (
                    <img
                      src={opt.imageUrl}
                      alt=""
                      style={{ height: 40, verticalAlign: 'middle', marginRight: 12, borderRadius: 6 }}
                    />
                  )}
                  <span style={{ fontWeight: 500 }}>{opt.label}</span>
                  {opt.description && (
                    <div className="option-desc">{opt.description}</div>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Skip link */}
        {q.skippable && (
          <button className="skip-link" onClick={skip}>
            Skip this question →
          </button>
        )}

        <Blocks blocks={q.blocksAfter} />

        <div className="question-nav">
          <button className="btn btn-secondary btn-sm" onClick={goBack} disabled={idx === 0}>
            ← Back
          </button>
          <span className="question-hint">
            {isText ? 'Type your answer above' : isMulti ? 'Pick your answers, then Continue' : 'Tap an answer to continue'}
          </span>
        </div>
      </div>
    </>
  );
}
